import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { interpretarRespuestaCreacion } from '../../utils/HelpMediciones';

/**
 * Hook del patrón de panel de medición: valida, envía, bloquea el doble
 * envío e interpreta la respuesta del backend.
 * @param {{ valoresIniciales: Object, validar: Function, enviar: Function,
 *           numeroDocumento: string, evbi: number, onExito?: Function }} config
 * @returns {{ valores, errores, setValor, guardando, bloqueado, resultado, guardar,
 *            limpiar }}
 */
export default function usePanelMedicion({
	valoresIniciales,
	validar,
	enviar,
	numeroDocumento,
	evbi,
	onExito,
}) {
	const dispatch = useDispatch();
	const [valores, setValores] = useState(valoresIniciales);
	const [errores, setErrores] = useState({});
	const [guardando, setGuardando] = useState(false);
	const [resultado, setResultado] = useState(null);
	const montadoRef = useRef(true);
	// Bloqueo síncrono: dos eventos en el mismo tick verían el estado viejo de guardando
	const enCursoRef = useRef(false);
	const [bloqueado, setBloqueado] = useState(false);
	// Bloqueo síncrono tras un registro exitoso: se levanta solo con limpiar()
	const bloqueadoRef = useRef(false);

	useEffect(() => () => {
		montadoRef.current = false;
	}, []);

	const setValor = (campo, valor) => {
		setValores((prev) => ({ ...prev, [campo]: valor }));
		// Se limpian el error del campo editado y los de valores derivados
		// (claves de errores que no son campos editables, p. ej. porcentaje)
		const aLimpiar = Object.keys(errores).filter(
			(clave) => clave === campo || !Object.prototype.hasOwnProperty.call(valores, clave)
		);
		if (aLimpiar.length) {
			setErrores((prev) => {
				const siguiente = { ...prev };
				aLimpiar.forEach((clave) => delete siguiente[clave]);
				return siguiente;
			});
		}
	};

	const guardar = () => {
		if (enCursoRef.current || bloqueadoRef.current) {
			return;
		}
		const v = validar(valores);
		setErrores(v.errores);
		setResultado(null);
		if (!v.valido) {
			return;
		}
		enCursoRef.current = true;
		setGuardando(true);
		Promise.resolve()
			.then(() => enviar(dispatch, { numeroDocumento, evbi, ...v.datos }))
			// Un rechazo se trata como fallo de red: se muestra al operador
			.then((response) => response, () => undefined)
			.then((response) => {
				enCursoRef.current = false;
				if (!montadoRef.current) {
					return;
				}
				const r = interpretarRespuestaCreacion(response);
				if (r.ok) {
					bloqueadoRef.current = true;
					// Antes de setGuardando(false): fuera de un handler React 17 no agrupa
					// los setState y no debe pintarse un frame con Guardar habilitado
					setBloqueado(true);
				}
				setResultado({
					...r,
					respuesta: r.ok && response.respuesta != null ? response.respuesta : null,
				});
				setGuardando(false);
				if (r.ok && onExito) {
					onExito(response);
				}
			});
	};

	const limpiar = () => {
		// Con una petición en curso no se limpia (el botón ya está deshabilitado)
		if (enCursoRef.current) {
			return;
		}
		bloqueadoRef.current = false;
		setValores(valoresIniciales);
		setErrores({});
		setResultado(null);
		setBloqueado(false);
	};

	return { valores, errores, setValor, guardando, bloqueado, resultado, guardar, limpiar };
}
